import React from "react";
import style from "./../style.module.css";
import { Input, Button, Textarea } from "@my-scoot/component-library-legacy";
import { TEAMS_FIELDS_CONFIG } from "@my-scoot/exly-react-component-lib";
import { api } from "data";
import { logError } from "utils/error";
import constants from "constants/constants";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { refreshIframe } from "./CreatorMiscellaneousData/Utils";
import { WebpageCreationContext } from "ui/pages/TemplatePreview";
import {
  MiscellaneousConfig,
  SIDEBAR_SECTIONS,
  SIDEBAR_KEYS,
  SECTIONKEY_TO_MISC_KEY,
} from "ui/pages/TemplatePreview/constants";
import useSetMissceleneous from "./common/useSetMissceleneous";
import { getErrorValue } from "./common/validator";
import {
  INITIAL_VALUES,
  DEFAULT_TEXT_TEAMS,
  TEAMS_FORM_DATA,
  TEAMS_LABELS,
  TEAMS_PLACEHOLDERS,
  TEAMS_MESSAGES,
  TEAMS_DEFAULTS,
  TEAMS_ERROR_KEYS,
  TEAMS_DATA_KEYS,
} from "../sidebar.constant";
import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";
import {
  creator_miscellaneous_data_types,
  UPDATE_CREATOR_DATA_API,
} from "features/Common/modules/CreatorData/CreatorData.constants";
import SectionVisibilityToggle from "./hideShow";
import { handleVisibilityToggle } from "../utils";
import useVisibilityMutations from "../useVisibilityMutations.hook";
import { getUserID } from "utils/AuthUtil";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { useTeamsHandlers } from "./Teams/utils/useTeamsHandlers";

export const Teams = ({ disableAllEdits, setField, hiddenFields = [] }) => {
  const { notify } = useNotifications();

  const initial_nav_section_name_ref = React.useRef(null);
  const initial_section_name_ref = React.useRef(null);

  const { miscellaneous_data, fetchMiscellaneousData, set_miscellaneous_data } =
    React.useContext(WebpageCreationContext);
  const [section_name, set_section_name] = React.useState(INITIAL_VALUES.teams);
  const [nav_section_name, set_nav_section_name] = React.useState(
    INITIAL_VALUES.nav_teams
  );
  const [uuid, set_uuid] = React.useState(null);
  const [processing, set_processing] = React.useState(false);
  const [data, set_data] = React.useState({});
  const [error_object, set_error_object] = React.useState({});
  const [visible, set_visible] = React.useState(true);
  const webpageContext = React.useContext(WebpageCreationContext);
  const currentTemplate = webpageContext?.templateData.title;

  const { createMutation, updateMutation } = useVisibilityMutations();

  const fieldsVisibility =
    TEAMS_FIELDS_CONFIG?.[(currentTemplate || "")?.toUpperCase()] ||
    TEAMS_FIELDS_CONFIG.DEFAULT;

  React.useEffect(() => {
    assignData();
  }, []);

  React.useEffect(() => {
    if (!checkIsArrayEmpty(miscellaneous_data?.teams)) {
      const [teamsData] = miscellaneous_data.teams;
      if (teamsData?.uuid) {
        set_uuid(teamsData?.uuid);
      }
    }
  }, [miscellaneous_data]);

  useSetMissceleneous({
    listeners: [data, nav_section_name, section_name, uuid, visible],
    data: {
      ...miscellaneous_data,
      teams: [
        {
          ...(uuid && { uuid }),
          data: {
            visible,
            custom_team_section_title: section_name,
            lead_keys: {
              custom_team_navbar_title: nav_section_name,
              visible,
            },
            data: {
              heading: data?.heading,
              description: data?.description,
            },
          },
        },
      ],
    },
    updateHandler: {
      teams: {
        handleSave: () => handleSave(),
      },
    },
  });

  const assignData = async () => {
    try {
      if (checkIsArrayEmpty(miscellaneous_data?.teams)) return;
      let temp = miscellaneous_data?.teams[0];
      set_data(temp?.data?.data);
      const sectionTitle = temp?.data?.custom_team_section_title;
      set_section_name(sectionTitle);
      initial_section_name_ref.current = sectionTitle;

      const navTitle =
        temp?.data?.lead_keys?.custom_team_navbar_title || DEFAULT_TEXT_TEAMS;
      set_nav_section_name(navTitle);
      initial_nav_section_name_ref.current = navTitle;

      set_uuid(temp?.uuid);

      // SAFETY NET: default visible = true
      set_visible(temp?.data?.visible ?? true);
    } catch (err) {
      notify(
        err?.body?.message ? err?.body?.message : constants.error_message,
        notification_color_keys.error
      );
    }
  };

  const updateNavbarTitleMutation = useCustomMutation({
    url: `${UPDATE_CREATOR_DATA_API}/${getUserID()}`,
    onSuccess: () => {
      initial_nav_section_name_ref.current = nav_section_name;
    },
    onError: (error) => {
      logError({
        error: TEAMS_MESSAGES.NAVBAR_UPDATE_ERROR,
        details: error,
        when: TEAMS_MESSAGES.NAVBAR_UPDATE_WHEN,
        occuredAt: TEAMS_MESSAGES.NAVBAR_UPDATE_LOCATION,
      });
      set_error_object((prev) => ({
        ...prev,
        nav_section_name: TEAMS_MESSAGES.NAVBAR_UPDATE_FAILED,
      }));
    },
  });

  const updateSectionTitleMutation = useCustomMutation({
    url: `${UPDATE_CREATOR_DATA_API}/${getUserID()}`,
    onSuccess: () => {
      initial_section_name_ref.current = section_name;
    },
    onError: (error) => {
      logError({
        error: TEAMS_MESSAGES.SECTION_TITLE_UPDATE_ERROR,
        details: error,
        when: TEAMS_MESSAGES.SECTION_TITLE_UPDATE_WHEN,
        occuredAt: TEAMS_MESSAGES.SECTION_TITLE_UPDATE_LOCATION,
      });
      set_error_object((prev) => ({
        ...prev,
        section_name: TEAMS_MESSAGES.SECTION_TITLE_UPDATE_FAILED,
      }));
    },
  });

  const createMiscellaneousMutation = useCustomMutation({
    url: api.creator_miscellaneous_data.create,
    onSuccess: (data, variables) => {
      refreshIframe();
      set_data({ ...variables.temp });
      set_error_object({});
      fetchMiscellaneousData({
        type: creator_miscellaneous_data_types.TEAMS,
      });
      notify(TEAMS_MESSAGES.SAVE_SUCCESS, notification_color_keys.success);
    },
  });

  const updateMiscellaneousMutation = useCustomMutation({
    url: api.creator_miscellaneous_data.update,
    onSuccess: (data, variables) => {
      refreshIframe();
      set_data({ ...variables.temp });
      set_error_object({});
      let context_data = miscellaneous_data.teams;
      context_data[0].data.data = variables.temp;
      set_miscellaneous_data({
        ...miscellaneous_data,
        teams: context_data,
      });
      notify(TEAMS_MESSAGES.SAVE_SUCCESS, notification_color_keys.success);
    },
  });

  const handleSave = async () => {
    if (processing) return;
    set_processing(true);

    try {
      let temp = { ...data };
      if (temp?.draft) {
        delete temp.draft;
      }

      let api_uuid = uuid;
      if (!checkIsArrayEmpty(miscellaneous_data.teams)) {
        const [teamsData] = miscellaneous_data.teams;
        if (teamsData?.uuid) {
          api_uuid = teamsData?.uuid;
        }
      }

      if (nav_section_name !== initial_nav_section_name_ref.current) {
        await updateNavbarTitleMutation.mutateAsync({
          source: TEAMS_DEFAULTS.SOURCE,
          updated_values: {
            lead_keys: {
              custom_team_navbar_title: nav_section_name,
            },
          },
        });
      }

      if (section_name !== initial_section_name_ref.current) {
        await updateSectionTitleMutation.mutateAsync({
          source: TEAMS_DEFAULTS.SOURCE,
          updated_values: {
            lead_keys: {
              custom_team_section_title: section_name,
            },
          },
        });
      }

      if (checkIsArrayEmpty(api_uuid)) {
        await createMiscellaneousMutation.mutateAsync({
          type: creator_miscellaneous_data_types.TEAMS,
          data: {
            visible,
            lead_keys: {
              custom_team_navbar_title: nav_section_name,
            },
            custom_team_section_title: section_name,
            data: temp,
            section_name,
          },
          temp,
        });
      } else {
        await updateMiscellaneousMutation.mutateAsync({
          uuid: api_uuid,
          updated_values: {
            data: {
              visible,
              lead_keys: {
                custom_team_navbar_title: nav_section_name,
              },
              custom_team_section_title: section_name,
              data: temp,
            },
          },
          temp,
        });
      }
    } catch (err) {
      logError({
        error: TEAMS_MESSAGES.SAVE_ERROR,
        when: TEAMS_MESSAGES.SAVE_ERROR_WHEN,
        occuredAt: TEAMS_MESSAGES.SAVE_ERROR_LOCATION,
      });
      notify(
        err?.body?.message ? err?.body?.message : constants.error_message,
        notification_color_keys.error
      );
    } finally {
      set_processing(false);
    }
  };

  const updateData = (value, key) => {
    let temp = data;
    temp[key] = value;
    set_data({ ...temp });
  };

  const headingError = getErrorValue(
    error_object,
    TEAMS_ERROR_KEYS.SECTION_HEADING,
    TEAMS_FORM_DATA.heading.key
  );
  const descriptionError = getErrorValue(
    error_object,
    TEAMS_ERROR_KEYS.SECTION_DESCRIPTION,
    TEAMS_FORM_DATA.description.key
  );

  const onToggleVisibility = () =>
    handleVisibilityToggle({
      currentVisible: visible,
      setVisible: set_visible,
      miscellaneousData: miscellaneous_data,
      sectionKey: SECTIONKEY_TO_MISC_KEY[SIDEBAR_KEYS.TEAMS],
      sectionType: creator_miscellaneous_data_types.TEAMS,
      defaultSectionName: SIDEBAR_SECTIONS[SIDEBAR_KEYS.TEAMS].title,
      fetchMiscellaneousData,
      set_miscellaneous_data,
      createMutation,
      updateMutation,
    });

  const {
    handleSectionNameChange,
    handleNavSectionNameChange,
    handleHeadingChange,
    handleDescriptionChange,
    showSectionTitle,
  } = useTeamsHandlers({
    error_object,
    set_error_object,
    set_section_name,
    set_nav_section_name,
    updateData,
    formData: TEAMS_FORM_DATA,
    setField,
    hiddenFields,
  });

  return (
    <div>
      <SectionVisibilityToggle
        visible={visible}
        disabled={disableAllEdits}
        onToggle={onToggleVisibility}
      />

      {fieldsVisibility.section_name && (
        <>
          <div className={style.label}>{TEAMS_LABELS.SECTION_TITLE}</div>
          <div className={`tw-mt-2 tw-mb-3`}>
            <Input
              fullWidth
              value={section_name}
              error={error_object.sectionNameLength}
              helperText={error_object.sectionNameLength}
              inputProps={{
                maxLength: MiscellaneousConfig.maxShortTitleLenght,
              }}
              onChange={handleSectionNameChange}
              disabled={disableAllEdits}
            />
          </div>

          {showSectionTitle(TEAMS_DATA_KEYS.NAVBAR_TITLE) && (
            <>
              <div className={style.label}>{TEAMS_LABELS.NAVBAR_TITLE}</div>
              <div className={`tw-mt-2 tw-mb-1`}>
                <Input
                  fullWidth
                  value={nav_section_name}
                  error={error_object.sectionNameLength}
                  helperText={error_object.sectionNameLength}
                  inputProps={{
                    maxLength: MiscellaneousConfig.maxShortTitleLenght,
                  }}
                  onChange={handleNavSectionNameChange}
                  disabled={disableAllEdits}
                />
              </div>
            </>
          )}
        </>
      )}

      {fieldsVisibility.heading && (
        <>
          <div className="tw-font-bold tw-text-[#666] tw-tracking-[0.2px] tw-text-sm tw-mt-3 tw-mb-1">
            {TEAMS_FORM_DATA.heading.label}
          </div>
          <Input
            error={error_object?.heading || error_object?.sectionHeading}
            helperText={headingError}
            placeholder={TEAMS_PLACEHOLDERS.HEADING}
            value={data?.heading}
            fullWidth
            className={style.placeholder}
            inputProps={{
              maxLength: MiscellaneousConfig.maxTitleLength,
            }}
            onChange={handleHeadingChange}
          />
        </>
      )}

      {fieldsVisibility.description && (
        <>
          <div className="tw-font-bold tw-text-[#666] tw-tracking-[0.2px] tw-text-sm tw-mt-3 tw-mb-1">
            {TEAMS_FORM_DATA.description.label}
          </div>
          <Textarea
            error={
              error_object?.description || error_object?.sectionDescription
            }
            helperText={descriptionError}
            placeholder={TEAMS_PLACEHOLDERS.DESCRIPTION}
            value={data?.description}
            rows={TEAMS_DEFAULTS.TEXTAREA_ROWS}
            customClasses={{ wrapper: style.placeholder }}
            onChange={handleDescriptionChange}
            minWidth={TEAMS_DEFAULTS.TEXTAREA_MIN_WIDTH}
          />
        </>
      )}

      <Button
        variant="contained"
        color="primary"
        disableElevation
        disabled={processing || disableAllEdits}
        loading={processing}
        onClick={handleSave}
        className="!tw-my-4 tw-float-right"
      >
        {TEAMS_LABELS.SAVE_BUTTON}
      </Button>
    </div>
  );
};
